import { useState, useCallback, useEffect } from 'react';
import { useFolderTree } from './folder.hooks';
import { useNavigate, useLocation } from 'react-router-dom';
import { parseNodeKey, NodeType } from '../utils/nodeId';
import { findFolder } from '../helpers/folder.utils';

// Session storage key
const FOLDER_VIEW_STATE_KEY = 'folderViewSessionState';

// Helper to load initial state from session storage
const loadInitialState = () => {
  try {
    const storedState = sessionStorage.getItem(FOLDER_VIEW_STATE_KEY);
    if (storedState) {
      const parsed = JSON.parse(storedState);
      // Ensure keys exist before returning and always include the root folder
      const expanded = Array.isArray(parsed.expandedNodeIds)
        ? Array.from(new Set([...parsed.expandedNodeIds, 'folder-root']))
        : ['folder-root'];
      return {
        selectedNodeIds: parsed.selectedNodeIds || [],
        expandedNodeIds: expanded,
      };
    }
  } catch (e) {
    console.error("Error reading folder view state from sessionStorage", e);
    sessionStorage.removeItem(FOLDER_VIEW_STATE_KEY);
  }
  return {
    selectedNodeIds: [],
    expandedNodeIds: ['folder-root'],
  };
};


export function useFolderData() {
  const { folders, loading, refetchFolders, preloadedTestCases, isUsingNewAPI } = useFolderTree();
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize state from session storage or defaults
  const [initialStateLoaded, setInitialStateLoaded] = useState(false);
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const [expandedNodeIds, setExpandedNodeIds] = useState<string[]>(['folder-root']);
  const [renamingNodeId, setRenamingNodeId] = useState<string>('');
  const [renameValue, setRenameValue] = useState<string>('');

  // Load state from session storage on mount
  useEffect(() => {
    const initialState = loadInitialState();
    setSelectedNodeIds(initialState.selectedNodeIds);
    setExpandedNodeIds(initialState.expandedNodeIds);
    // Note: loadedTestCases state will be managed elsewhere, but we read it here
    // setLoadedTestCases(initialState.loadedTestCases); // Moved to useProjectItems
    setInitialStateLoaded(true);
  }, []);

  // Save state to session storage whenever it changes
  useEffect(() => {
    if (initialStateLoaded) {
      try {
        // Only save selection and expansion here
        const stateToSave = JSON.stringify({ selectedNodeIds, expandedNodeIds });
        if(localStorage.getItem('showEditorwarning') === 'false' ){
          sessionStorage.setItem(FOLDER_VIEW_STATE_KEY, stateToSave);
        };
       
      } catch (e) {
        console.error("Error saving folder view state to sessionStorage", e);
      }
    }
  }, [selectedNodeIds, expandedNodeIds, initialStateLoaded]);

  const handleSelectedItemsChange = useCallback((_event: React.SyntheticEvent | null, itemIds: string[] | string) => {
    const ids = Array.isArray(itemIds) ? itemIds : [itemIds];
    if(localStorage.getItem('showEditorwarning') === 'false'){
      setSelectedNodeIds(ids);
    }

    // Navigation logic - only navigate if we're not already at the target URL
    if (ids.length === 1) {
      try {
        const { id, type } = parseNodeKey(ids[0]);
        if (type === 'project') {
          const targetPath = `/folders/project/${id}`;
          if(localStorage.getItem('showEditorwarning') === 'false'){
            if (location.pathname !== targetPath) {
              navigate(targetPath);
            }
          }
          
        } else if (type === 'folder') {
          // Navigate to base folder view if a folder or root is selected
          if(localStorage.getItem('showEditorwarning') === 'false'){
            if (location.pathname !== '/folders') {
              navigate('/folders');
            }
          }   
        } else if (type === 'testcase') {
          // Navigation for test cases is handled separately
        } else {
          // Fallback to base folder view for any other type
          if (location.pathname !== '/folders') {
            navigate('/folders');
          }
        }
      } catch (e) {
        console.error(`Error parsing node key for navigation: ${ids[0]}`, e);
        // Fallback navigation if parsing fails
        if (location.pathname !== '/folders') {
          navigate('/folders');
        }
      }
    } else {
      // Navigate to base folder view if multiple items or no items are selected
      if (location.pathname !== '/folders') {
        navigate('/folders');
      }
    }
  }, [navigate, location.pathname]);

  const handleExpandedItemsChange = useCallback(
    (_event: React.SyntheticEvent, itemIds: string[]) => {
      const idsSet = new Set(itemIds);
      console.log("Expanded items changed:", itemIds);
      idsSet.add('folder-root'); // Root should always remain expanded
      setExpandedNodeIds(Array.from(idsSet));
    },
    []
  );

  // --- Renaming Logic ---
  const startRenamingFolder = useCallback((nodeId: string) => {
    try {
      const { id } = parseNodeKey(nodeId);
      const folder = findFolder(folders, id);
      if (folder) {
        setRenamingNodeId(nodeId);
        setRenameValue(folder.name);
      } else {
         console.warn(`Folder not found for renaming: ${nodeId}`);
         setRenamingNodeId(''); // Ensure state is cleared if folder not found
      }
    } catch (e) {
        console.error(`Error parsing node ID for renaming: ${nodeId}`, e);
        setRenamingNodeId('');
    }
  }, [folders]); // Depends on the current folder structure

  const handleRenameChange = useCallback((value: string) => {
    setRenameValue(value);
  }, []);

  const handleRenameCancel = useCallback(() => {
    setRenamingNodeId('');
    setRenameValue(''); // Clear value on cancel
  }, []);

  // Note: handleRenameSubmit will be part of useFolderOperations as it involves an API call

  return {
    folders,
    loading,
    refetchFolders,
    selectedNodeIds,
    setSelectedNodeIds, // Expose setter if needed externally (e.g., after delete)
    handleSelectedItemsChange,
    expandedNodeIds,
    setExpandedNodeIds, // Expose setter if needed (e.g., after create)
    handleExpandedItemsChange,
    renamingNodeId,
    renameValue,
    startRenamingFolder, // Renamed from handleRenameFolder for clarity
    handleRenameChange,
    handleRenameCancel,
    preloadedTestCases, // Pass through for projectItems hook
    isUsingNewAPI, // Pass through for projectItems hook
  };
} 